import { useCallback } from 'react';

import { CheckboxFieldAdapter } from '@zola/zola-ui/src/components/Form/CheckboxField';

import { Field, useForm } from 'react-final-form';

import { FormWithMutators } from '~/components/common/form/formWithMutators';
import {
  NumberInputFieldProps,
  NumberInputField,
} from '~/components/common/form/NumberInputField/NumberInputField';

import styles from './numberFieldWithFlexibility.module.less';

export type NumberFieldWithFlexibilityProps<FormValues extends Record<string, any>> = Omit<
  NumberInputFieldProps<FormValues>,
  'inputOnChange' | 'className'
> & {
  /** If provided, a checkbox for "This is flexible" will be include */
  flexibleFieldName?: keyof FormValues;

  /** Optional onChange function to pass to price field */
  onChangeFunction?: () => void;

  /** If provided, a checkbox for "We're still deciding" will be include */
  stillDecidingFieldName?: keyof FormValues;
};

/**
 * Ask the couple for a number, either just a number  it can be a monetary value.
 * Additionally, the number can be flexible or the couple may not know, in which
 * case they can click "We're still deciding"
 *
 * The we're still deciding and main input fields are linked, modifying one will
 * clear the other and vice versa.
 *
 * @param props
 * @returns
 */
export const NumberFieldWithFlexibility = <FormValues extends Record<string, any>>(
  props: NumberFieldWithFlexibilityProps<FormValues>
): JSX.Element => {
  const {
    name,
    label,
    placeholder,
    type = 'number',
    required = false,
    maxValue,
    requiredCustomValidator,
    flexibleFieldName,
    stillDecidingFieldName,
    onChangeFunction,
    ...rest
  } = props;

  const form = useForm<FormValues>() as FormWithMutators<FormValues>;
  const { clear } = form.mutators;

  const inputOnChange = useCallback(() => {
    if (stillDecidingFieldName && !required) {
      clear(stillDecidingFieldName);
    }
    if (onChangeFunction) {
      onChangeFunction();
    }
  }, [clear, onChangeFunction, required, stillDecidingFieldName]);

  return (
    <div className={styles.numberFieldWithFlexibility}>
      <NumberInputField
        name={name}
        label={label}
        placeholder={placeholder}
        type={type}
        required={required}
        maxValue={maxValue}
        requiredCustomValidator={requiredCustomValidator}
        inputOnChange={inputOnChange}
        inputMode="numeric"
        {...rest}
      />
      {flexibleFieldName && (
        <Field
          className={styles.checkbox}
          id={flexibleFieldName}
          name={flexibleFieldName as string}
          label="This is flexible"
          component={CheckboxFieldAdapter}
          type="checkbox"
          onChange={(event: React.ChangeEvent) => {
            const target = event.target as HTMLInputElement;
            if (target.checked && stillDecidingFieldName) {
              clear(stillDecidingFieldName);
            }
          }}
        />
      )}
      {stillDecidingFieldName && (
        <Field
          className={styles.checkbox}
          id={stillDecidingFieldName}
          name={stillDecidingFieldName as string}
          label="We're still deciding"
          component={CheckboxFieldAdapter}
          type="checkbox"
          onChange={(event: React.ChangeEvent) => {
            const target = event.target as HTMLInputElement;
            if (target.checked && !required) {
              clear(name);

              if (flexibleFieldName) {
                clear(flexibleFieldName);
              }
            }
          }}
        />
      )}
    </div>
  );
};
